import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ReactNode } from "react";
import DndSortOverlay from "./internal/DndSortOverlay";
import announcements from "./internal/announcements";
import { DndSortChangeEvent } from "./types";
import DndSortContextProvider from "./internal/DndSortContext";
import collisionDetection from "./internal/collisionDetection";
import getNextCoordinates from "./internal/getNextCoordinates";

/**
 * Props for the DndSort component
 */
export type DndSortProps = {
  /**
   * Callback function that is called when an item is dropped.
   * Provides information about the drop event including source, destination, and validity.
   */
  onDrop?: (event: DndSortChangeEvent) => void;
  /**
   * The content to be rendered inside the DndSort context.
   * Should include DndSortZone and DndSortCard components.
   */
  children: ReactNode;
};

/**
 * Root component for drag and drop sorting functionality.
 *
 * Features:
 * - Provides drag and drop context for child components
 * - Supports both mouse and touch interactions
 * - Keyboard accessibility with arrow key navigation
 * - Automatic collision detection and positioning
 * - Screen reader announcements for accessibility
 * - Flexible drop event handling
 * - Supports multiple drop zones and draggable items
 * - Visual feedback during drag operations
 *
 * @example
 * <DndSort onDrop={handleDrop}>
 *   <DndSortZone id="zone-1" label="Zone 1">
 *     <DndSortCard id="item-1" label="Item 1">
 *       Item content
 *     </DndSortCard>
 *   </DndSortZone>
 * </DndSort>
 */
export default function DndSort({
  children,
  onDrop = () => {
    return;
  },
}: DndSortProps) {
  const sensors = useSensors(
    useSensor(PointerSensor), // Pointer covers both mouse and touch
    useSensor(KeyboardSensor, {
      coordinateGetter: getNextCoordinates,
    }),
  );

  return (
    <DndContext
      accessibility={{ announcements }}
      collisionDetection={collisionDetection}
      sensors={sensors}
    >
      <DndSortContextProvider onDrop={onDrop}>
        {children}
        <DndSortOverlay />
      </DndSortContextProvider>
    </DndContext>
  );
}
